/**
 * Demandes client : ce qu'on en lit dans la file, la fiche et la saisie, sans
 * rien dessiner (tests/unit/interventions-list.test.ts).
 */
import { EMAIL_INVALID_MESSAGE, OPEN_REQUEST_STATES, isValidEmail } from '@myequip/shared';
import { categoryLabel, stateLabel } from '../../core/i18n.js';

/** Une réponse du demandeur à la question du bureau. */
export interface RequestReply {
  id: string;
  body: string;
  photos: number;
  created_at: string;
}

export interface ServiceRequest {
  id: string;
  reference: string;
  status: string;
  origin?: string;
  requester_name: string;
  requester_email: string | null;
  requester_phone: string | null;
  symptom_code: string | null;
  description: string | null;
  perceived_urgency: string;
  created_at: string;
  customer_id: string | null;
  customer_name: string | null;
  site_id: string | null;
  site_label: string | null;
  site_address?: string | null;
  equipment_id: string | null;
  equipment_category: string | null;
  brand: string | null;
  model: string | null;
  qualification_notes?: string | null;
  rejected_reason?: string | null;
  converted_intervention_id?: string | null;
  created_by_name?: string | null;
  /** Réponses écrites par le demandeur depuis sa page de suivi (les plus récentes d'abord). */
  replies?: RequestReply[];
}

/** Une demande qui attend encore le bureau (la file « Demandes ») : état non terminal, pas convertie. */
export function isOpenRequest(request: Pick<ServiceRequest, 'status' | 'converted_intervention_id'>): boolean {
  return (OPEN_REQUEST_STATES as readonly string[]).includes(request.status) && !request.converted_intervention_id;
}

export function isUrgentRequest(request: Pick<ServiceRequest, 'perceived_urgency'>): boolean {
  return request.perceived_urgency === 'critique' || request.perceived_urgency === 'haute';
}

/** « Urgence haute » : l'urgence telle que le client la ressent. */
export function urgencyLabel(urgency: string): string {
  return `Urgence ${stateLabel(urgency).toLowerCase()}`;
}

export function equipmentLabel(request: Pick<ServiceRequest, 'equipment_category' | 'brand' | 'model'>): string | null {
  if (!request.equipment_category) return null;
  return [categoryLabel(request.equipment_category), request.brand, request.model].filter(Boolean).join(' ');
}

/** Qui a fait la demande, comme on le reconnaît : le client, sinon le contact libre. */
export function requestWho(request: Pick<ServiceRequest, 'customer_name' | 'requester_name'>): string {
  return request.customer_name ?? request.requester_name;
}

/** Où : le site, sinon comment joindre le contact libre. */
export function requestWhere(
  request: Pick<ServiceRequest, 'site_label' | 'requester_phone' | 'requester_email' | 'customer_id'>,
): string {
  if (request.site_label) return request.site_label;
  const contact = request.requester_phone ?? request.requester_email;
  return contact ? `Contact : ${contact}` : 'Contact sans coordonnées';
}

const ORIGINS: Record<string, string> = { qr: 'QR code', portail: 'portail client', bureau: 'téléphone' };

export function originLabel(origin: string | undefined): string | null {
  return origin ? (ORIGINS[origin] ?? null) : null;
}

/**
 * Âge d'une demande, lu d'un coup d'œil : « à l'instant », « il y a 12 min »,
 * « il y a 3 h », « hier », « il y a 4 jours ».
 */
export function requestAge(createdAt: string, now = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(createdAt).getTime()) / 60_000));
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'hier' : `il y a ${days} jours`;
}

/** La file « Demandes » de l'espace Interventions. */
export const REQUESTS_TAB = '/interventions?onglet=demandes';

/**
 * « Planifier » : la page de nouvelle intervention, préremplie par la demande
 * (contrat avec le planning du 17/10/2026), avec le retour vers la file.
 */
export function planRequestPath(requestId: string, from = REQUESTS_TAB): string {
  const params = new URLSearchParams({ demande: requestId, depuis: from });
  return `/planning/nouvelle-intervention?${params.toString()}`;
}

/** Ancien nom de la demande ouverte dans `/demandes?…`, porté par des avis et des urgences. */
const LEGACY_REQUEST_PARAM = 'requestId';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Où mène l'ancienne adresse `/demandes` (avant le 17/10/2026) : la fiche de la
 * demande désignée, sinon la file « Demandes », les autres paramètres gardés.
 */
export function legacyRequestsTarget(search: string): string {
  const params = new URLSearchParams(search);
  const requestId = params.get(LEGACY_REQUEST_PARAM) ?? '';
  if (UUID.test(requestId)) return `/interventions/demandes/${requestId}`;
  params.delete(LEGACY_REQUEST_PARAM);
  params.set('onglet', 'demandes');
  return `/interventions?${params.toString()}`;
}

/** « Planifier plus tard » a besoin d'un client et d'un site : sans eux, on planifie en les choisissant. */
export function canPlanLater(request: Pick<ServiceRequest, 'customer_id' | 'site_id'>): boolean {
  return Boolean(request.customer_id && request.site_id);
}

/**
 * Ce qui empêche d'enregistrer l'appelant d'une demande notée au bureau, avec
 * les bornes de `POST /service-requests` (nom 2 à 120 caractères, téléphone 6
 * à 20, e-mail valide) : une phrase qui guide plutôt qu'un refus du serveur.
 * Un contact libre doit pouvoir être rappelé ; un client connu peut se passer
 * de nom et de téléphone. `null` quand tout passe.
 */
export function callerProblem(caller: { known: boolean; name: string; phone: string; email: string }): string | null {
  const name = caller.name.trim();
  const phone = caller.phone.trim();
  const email = caller.email.trim();
  if (!caller.known && !name) return 'Indiquez le nom de la personne.';
  if (name && (name.length < 2 || name.length > 120)) return 'Le nom compte de 2 à 120 caractères.';
  if (phone && (phone.length < 6 || phone.length > 20)) return 'Le téléphone compte de 6 à 20 caractères.';
  if (email && !isValidEmail(email)) return EMAIL_INVALID_MESSAGE;
  if (!caller.known && !phone && !email) return 'Indiquez un téléphone ou un e-mail pour la rappeler.';
  return null;
}
